import { useState, type ReactNode } from 'react'
import clsx from 'clsx'
import {
  ArrowUp,
  AudioLines,
  ChevronDown,
  HelpCircle,
  Loader2,
  RotateCcw,
  SlidersHorizontal,
  Waves,
  Zap,
} from 'lucide-react'

import type { AIModel } from '@/api/types'
import {
  MINIMAX_PROMPT_MAX,
  MINIMAX_SFX_OPTIONS,
  defaultMiniMaxParams,
  resolveMiniMaxParams,
} from '@/features/studio/canvas/audio-params'
import { miniMaxVoiceLabel } from '@/features/studio/canvas/minimax-voices'
import { MiniMaxVoiceLibraryDialog } from '@/features/studio/canvas/nodes/MiniMaxVoiceLibraryDialog'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'
import { buildCanvasModelOptions } from '@/features/studio/canvas/model-options'

type CanvasT = (key: string, opts?: Record<string, unknown>) => string

export function MiniMaxSpeechPanel({
  modelId,
  models,
  params,
  promptLength,
  cost,
  runDisabled,
  runRunning,
  onModelChange,
  onParamsChange,
  onRun,
  t,
}: {
  modelId: string
  models: AIModel[]
  params?: Record<string, string | number>
  promptLength: number
  cost: number
  runDisabled?: boolean
  runRunning?: boolean
  onModelChange: (modelId: string) => void
  onParamsChange: (patch: Record<string, string | number>) => void
  onRun: () => void
  t: CanvasT
}) {
  const resolved = resolveMiniMaxParams(params)
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [basicOpen, setBasicOpen] = useState(true)
  const [effectOpen, setEffectOpen] = useState(true)
  const [sfxOpen, setSfxOpen] = useState(true)
  const modelOptions = buildCanvasModelOptions(models, t)
  const voiceLabel = miniMaxVoiceLabel(resolved.voice_id, t('canvas.params.mmVoiceQingse'))
  const used = Math.min(promptLength, MINIMAX_PROMPT_MAX)

  const resetBasic = () => {
    const d = defaultMiniMaxParams()
    onParamsChange({ speed: d.speed, pitch: d.pitch, volume: d.volume })
  }

  const resetEffects = () => {
    const d = defaultMiniMaxParams()
    onParamsChange({ modify_pitch: d.modify_pitch, intensity: d.intensity, timbre: d.timbre })
  }

  return (
    <div className="cn-gen-controls cn-minimax nodrag nopan nowheel">
      <div className="cn-minimax__bar">
        <span className="cn-minimax__model-icon" aria-hidden>
          <AudioLines className="h-3.5 w-3.5" />
        </span>
        <ProtoDropdown
          compact
          portal
          className="cn-minimax__model"
          value={modelId}
          onValueChange={onModelChange}
          options={modelOptions}
          disabled={!modelOptions.length}
          placeholder={t('canvas.section.model')}
        />

        <button
          type="button"
          className={clsx('cn-minimax__voice-pill', voiceOpen && 'cn-minimax__voice-pill--open')}
          onClick={() => setVoiceOpen(true)}
        >
          <Waves className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate">{voiceLabel}</span>
        </button>

        <button
          type="button"
          className="cn-minimax__icon-btn"
          aria-label={t('canvas.minimaxSpeech.advancedToggle')}
          onClick={() => setEffectOpen((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>

        <span className="cn-minimax__count" title={t('canvas.minimaxSpeech.promptLimitHint')}>
          {used}/{MINIMAX_PROMPT_MAX}
        </span>
        <span className="cn-minimax__cost" title={t('canvas.modelCost', { cost })}>
          <Zap className="h-3 w-3" />
          {cost}
        </span>
        <button
          type="button"
          className="cn-minimax__run"
          disabled={runDisabled}
          onClick={onRun}
          aria-label={t('canvas.run')}
        >
          {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-3.5 w-3.5" />}
        </button>
      </div>

      <Section
        title={t('canvas.minimaxSpeech.basic')}
        open={basicOpen}
        onToggle={() => setBasicOpen((v) => !v)}
        action={
          <button type="button" className="cn-minimax__reset" onClick={resetBasic}>
            <RotateCcw className="h-3 w-3" />
            {t('canvas.minimaxSpeech.reset')}
          </button>
        }
      >
        <SliderRow
          label={t('canvas.params.speed')}
          min={0.5}
          max={2}
          step={0.01}
          value={resolved.speed}
          display={resolved.speed.toFixed(2)}
          onChange={(speed) => onParamsChange({ speed })}
        />
        <SliderRow
          label={t('canvas.params.pitchTone')}
          min={-12}
          max={12}
          step={1}
          value={resolved.pitch}
          display={String(Math.round(resolved.pitch))}
          onChange={(pitch) => onParamsChange({ pitch })}
        />
        <SliderRow
          label={t('canvas.params.volume')}
          min={0.1}
          max={10}
          step={0.1}
          value={resolved.volume}
          display={resolved.volume.toFixed(1)}
          onChange={(volume) => onParamsChange({ volume })}
        />
      </Section>

      <Section
        title={t('canvas.minimaxSpeech.voiceEffects')}
        open={effectOpen}
        onToggle={() => setEffectOpen((v) => !v)}
        action={
          <button type="button" className="cn-minimax__reset" onClick={resetEffects}>
            <RotateCcw className="h-3 w-3" />
            {t('canvas.minimaxSpeech.reset')}
          </button>
        }
      >
        <SliderRow
          label={t('canvas.params.modifyPitch')}
          hint={t('canvas.minimaxSpeech.modifyPitchHint')}
          min={-100}
          max={100}
          step={1}
          value={resolved.modify_pitch}
          display={String(Math.round(resolved.modify_pitch))}
          onChange={(modify_pitch) => onParamsChange({ modify_pitch })}
        />
        <SliderRow
          label={t('canvas.params.modifyIntensity')}
          hint={t('canvas.minimaxSpeech.modifyIntensityHint')}
          min={-100}
          max={100}
          step={1}
          value={resolved.intensity}
          display={String(Math.round(resolved.intensity))}
          onChange={(intensity) => onParamsChange({ intensity })}
        />
        <SliderRow
          label={t('canvas.params.modifyTimbre')}
          hint={t('canvas.minimaxSpeech.modifyTimbreHint')}
          min={-100}
          max={100}
          step={1}
          value={resolved.timbre}
          display={String(Math.round(resolved.timbre))}
          onChange={(timbre) => onParamsChange({ timbre })}
        />
      </Section>

      <Section
        title={t('canvas.params.soundEffects')}
        open={sfxOpen}
        onToggle={() => setSfxOpen((v) => !v)}
      >
        <div className="cn-minimax__chips">
          {MINIMAX_SFX_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={clsx(
                'cn-minimax__chip',
                resolved.sound_effects === opt.value && 'cn-minimax__chip--on',
              )}
              onClick={() => onParamsChange({ sound_effects: opt.value })}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </Section>

      <MiniMaxVoiceLibraryDialog
        open={voiceOpen}
        onOpenChange={setVoiceOpen}
        selectedVoiceId={resolved.voice_id}
        onSelect={(voice_id) => onParamsChange({ voice_id })}
        t={t}
      />
    </div>
  )
}

function Section({
  title,
  open,
  onToggle,
  action,
  children,
}: {
  title: string
  open: boolean
  onToggle: () => void
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="cn-minimax__section">
      <div className="cn-minimax__section-head">
        <button type="button" className="cn-minimax__section-toggle" onClick={onToggle}>
          <ChevronDown className={clsx('h-3.5 w-3.5 transition', open && 'rotate-180')} />
          <span>{title}</span>
        </button>
        {action}
      </div>
      {open ? <div className="cn-minimax__section-body">{children}</div> : null}
    </div>
  )
}

function SliderRow({
  label,
  hint,
  min,
  max,
  step,
  value,
  display,
  onChange,
}: {
  label: string
  hint?: string
  min: number
  max: number
  step: number
  value: number
  display: string
  onChange: (value: number) => void
}) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="cn-minimax__slider">
      <span className="cn-minimax__slider-label">
        {label}
        {hint ? (
          <span className="cn-minimax__hint" title={hint}>
            <HelpCircle className="h-3 w-3" />
          </span>
        ) : null}
      </span>
      <input
        type="range"
        className="cn-minimax__range"
        style={{ ['--mm-pct' as string]: `${pct}%` }}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      />
      <input
        type="number"
        className="cn-minimax__num"
        min={min}
        max={max}
        step={step}
        value={display}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (Number.isFinite(n)) onChange(n)
        }}
        aria-label={label}
      />
    </div>
  )
}
